import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Upload, CheckCircle, XCircle, Loader2, FileText, Shield, AlertCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ClientUpload — public, token-only page where a client uploads documents in
 * response to an agent's request. No login required; the URL token IS the
 * credential. The page is mobile-first and Utmost-branded.
 */
export default function ClientUpload() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [items, setItems] = useState([]); // [{file, status, result, error}]
  const [completing, setCompleting] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await fetch(`${API_URL}/api/client-doc-requests/${token}`);
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErr(d.detail || 'Link is invalid or expired.');
        return;
      }
      setInfo(await r.json());
    } catch (e) {
      setErr('Network error. Please refresh and try again.');
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const acceptList = '.pdf,application/pdf,image/jpeg,image/jpg,image/png,image/heic,image/heif';

  const onFilesSelected = (files) => {
    const list = Array.from(files);
    setItems(prev => [...prev, ...list.map(file => ({ file, status: 'pending' }))]);
    // Kick off uploads sequentially to avoid hammering the LLM
    list.forEach((file) => uploadOne(file));
  };

  const uploadOne = async (file) => {
    setItems(prev => prev.map(it => it.file === file ? { ...it, status: 'uploading' } : it));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/client-doc-requests/${token}/upload`, {
        method: 'POST', body: fd,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `Upload failed (${r.status})`);
      setItems(prev => prev.map(it => it.file === file ? { ...it, status: 'done', result: d.file } : it));
    } catch (e) {
      setItems(prev => prev.map(it => it.file === file ? { ...it, status: 'failed', error: e.message || 'Upload failed' } : it));
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const r = await fetch(`${API_URL}/api/client-doc-requests/${token}/complete`, { method: 'POST' });
      if (!r.ok) throw new Error('Could not mark as complete');
      setDone(true);
    } catch (e) {
      setErr(e.message || 'Failed to complete');
    }
    setCompleting(false);
  };

  // ---------- render states ----------
  if (err && !info) return <ErrorScreen msg={err} />;
  if (!info) return <LoadingScreen />;
  if (done) return <ThankYouScreen advisor={info.advisor} count={items.filter(i => i.status === 'done').length} />;

  const allDone = items.length > 0 && items.every(i => i.status === 'done' || i.status === 'failed');
  const successCount = items.filter(i => i.status === 'done').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <header className="bg-gradient-to-r from-[#003D5C] to-[#0077B6] text-white px-4 py-5 border-b-4 border-[#D4AF37]">
        <div className="max-w-2xl mx-auto">
          <div className="text-xs tracking-wider text-blue-100 uppercase">Utmost Financial Inc.</div>
          <h1 className="text-xl sm:text-2xl font-bold mt-1">Secure Document Upload</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        <section className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <Shield className="h-6 w-6 text-[#0077B6] mt-1 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-700">
                Hi {info.request.client_name ? <b>{info.request.client_name}</b> : 'there'} — your advisor{' '}
                <b>{info.advisor.name}</b> is requesting documents for your application.
              </p>
              {info.request.custom_message && (
                <blockquote className="mt-2 text-sm text-gray-700 border-l-4 border-[#0077B6] pl-3 italic">
                  {info.request.custom_message}
                </blockquote>
              )}
              <p className="text-xs text-gray-500 mt-3">
                We'll automatically organise your files into the right category. Accepted: PDF, JPG, PNG, HEIC.
                Max 15 MB each. Your upload is encrypted in transit and only your advisor can see it.
              </p>
            </div>
          </div>
        </section>

        <section
          onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-[#0077B6]'); }}
          onDragLeave={(e) => { e.currentTarget.classList.remove('ring-2', 'ring-[#0077B6]'); }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.classList.remove('ring-2', 'ring-[#0077B6]');
            if (e.dataTransfer.files?.length) onFilesSelected(e.dataTransfer.files);
          }}
          className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-8 text-center transition"
          data-testid="dropzone"
        >
          <Upload className="h-10 w-10 text-[#0077B6] mx-auto mb-2" />
          <p className="text-gray-800 font-semibold">Drop your files here</p>
          <p className="text-sm text-gray-500 mt-1">or</p>
          <label className="mt-3 inline-block">
            <input
              type="file"
              multiple
              accept={acceptList}
              className="hidden"
              onChange={(e) => e.target.files && onFilesSelected(e.target.files)}
              data-testid="file-input"
            />
            <span className="inline-block bg-[#0077B6] text-white px-5 py-2 rounded-lg font-semibold cursor-pointer hover:bg-[#005f8a]">
              Choose Files
            </span>
          </label>
          <p className="text-xs text-gray-400 mt-3">PDF · JPG · PNG · HEIC up to 15 MB each</p>
        </section>

        {items.length > 0 && (
          <section className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-800 mb-3">Your uploads ({successCount}/{items.length} done)</h2>
            <ul className="space-y-2" data-testid="upload-items">
              {items.map((it, i) => <UploadRow key={`${it.file.name}-${i}`} item={it} categories={info.categories} />)}
            </ul>
            <button
              onClick={handleComplete}
              disabled={completing || !allDone || successCount === 0}
              className="mt-5 w-full bg-[#D4AF37] hover:bg-[#b8962e] disabled:opacity-50 text-[#003D5C] font-bold py-3 rounded-lg flex items-center justify-center gap-2"
              data-testid="complete-btn"
            >
              {completing ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle className="h-5 w-5" />}
              {completing ? 'Notifying your advisor…' : "I'm done uploading"}
            </button>
          </section>
        )}

        <p className="text-center text-xs text-gray-400 mt-6">
          Powered by Utmost Financial Inc. — your data stays private.
        </p>
      </main>
    </div>
  );
}

function UploadRow({ item, categories }) {
  const label = (val) => categories?.find(c => c.value === val)?.label || val;
  const ico = item.status === 'done' ? <CheckCircle className="h-5 w-5 text-green-600" />
    : item.status === 'failed' ? <XCircle className="h-5 w-5 text-red-600" />
    : <Loader2 className="h-5 w-5 text-[#0077B6] animate-spin" />;
  return (
    <li className="flex items-center gap-3 py-2 border-b last:border-0 text-sm" data-testid={`upload-row-${item.file.name}`}>
      {ico}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-800 truncate">{item.file.name}</p>
        <p className="text-xs text-gray-500">
          {item.status === 'done' && item.result && (
            <>Auto-categorised as <b>{label(item.result.category)}</b>
              {item.result.owner_name && <> · owner {item.result.owner_name}</>}
              {item.result.company && <> · {item.result.company}</>}
            </>
          )}
          {item.status === 'failed' && <span className="text-red-600">{item.error}</span>}
          {item.status === 'uploading' && 'Uploading & analysing…'}
          {item.status === 'pending' && 'Queued'}
        </p>
      </div>
    </li>
  );
}

function ThankYouScreen({ advisor, count }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="h-16 w-16 bg-green-50 rounded-full mx-auto flex items-center justify-center mb-4">
          <CheckCircle className="h-10 w-10 text-green-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Documents received</h1>
        <p className="text-gray-700">
          Thanks! Your <b>{count}</b> file{count === 1 ? '' : 's'} have been submitted securely.{' '}
          <b>{advisor.name}</b> will follow up shortly.
        </p>
        <p className="text-xs text-gray-400 mt-6">— Utmost Financial Inc.</p>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-[#0077B6]" />
    </div>
  );
}

function ErrorScreen({ msg }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-red-200 rounded-xl p-6 text-center">
        <AlertCircle className="h-10 w-10 text-red-600 mx-auto mb-2" />
        <h1 className="font-bold text-lg text-gray-900 mb-1">Link Unavailable</h1>
        <p className="text-sm text-gray-600">{msg}</p>
        <p className="text-xs text-gray-400 mt-4">Please contact your Utmost Financial advisor for a new link.</p>
      </div>
    </div>
  );
}
